// ChoiceSelect (DESIGN §8): a native select of suggested values (+ values already in use) whose last option,
// "Custom…", swaps in a text input — Enter or leaving the field keeps the typed value, Esc goes back to the list.
// Used for Role and Department on People and in the team member sheet.
import { useEffect, useRef, useState } from 'preact/hooks';
import { cx } from './ui.jsx';

const CUSTOM = '\u0000custom';

// Suggested values first, then the other values in use (alphabetical), then the current value if still missing.
export function choiceOptions(presets = [], inUse = [], current = '') {
  const seen = new Set(presets.map((p) => p.toLowerCase()));
  const extra = [...new Set(inUse.filter((v) => v && !seen.has(v.toLowerCase())))].sort((a, b) => a.localeCompare(b));
  const list = [...presets, ...extra];
  if (current && !list.some((v) => v.toLowerCase() === current.toLowerCase())) list.push(current);
  return list;
}

export function ChoiceSelect({
  value = '', options = [], onValue, label, placeholder = 'None', customPlaceholder = 'Type a name', maxLength = 80,
  size, disabled, name, class: cls, selectRef, ...rest
}) {
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const ownSelect = useRef(null);
  const refocus = useRef(false);
  const done = useRef(false);
  useEffect(() => {
    if (custom) inputRef.current?.focus();
    else if (refocus.current) (selectRef || ownSelect).current?.focus();
    refocus.current = false;
  }, [custom]);

  const pick = (e) => {
    const v = e.currentTarget.value;
    if (v === CUSTOM) {
      e.currentTarget.value = value; // keep showing the real value until a custom one is typed
      setDraft('');
      done.current = false;
      setCustom(true);
      return;
    }
    if (v !== value) onValue?.(v);
  };
  const finish = (keep) => {
    if (done.current) return; // Enter, then the blur of the input being removed
    done.current = true;
    const v = draft.trim();
    refocus.current = true;
    setCustom(false);
    if (keep && v && v !== value) onValue?.(v);
  };

  if (custom) {
    return (
      <input
        ref={inputRef}
        class={cx('input', size === 'sm' && 'input-sm', 'choice-input', cls)}
        value={draft}
        name={name}
        maxLength={maxLength}
        placeholder={customPlaceholder}
        aria-label={label ? `${label} (custom)` : undefined}
        autoComplete="off"
        onInput={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); finish(true); }
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); finish(false); }
        }}
        onBlur={() => finish(true)}
      />
    );
  }
  return (
    <select
      ref={selectRef || ownSelect}
      class={cx('select', size === 'sm' && 'input-sm', 'choice-select', !value && 'is-empty', cls)}
      value={value}
      name={name}
      aria-label={label}
      disabled={disabled}
      onChange={pick}
      {...rest}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
      <option value={CUSTOM}>Custom…</option>
    </select>
  );
}
